// ============================================================
// Consent — geo default rules (DATA, not code). docs/consent.md
//
// One row per legal regime. The visitor's country comes from Cloudflare's
// `request.cf.country` at ingest, never from anything the client sends.
// Adding or changing a region means editing this table: `consentRuleFor`
// is a lookup, and every consumer (the /v2/consent config endpoint, the
// receipt stamp, the banner) reads the row it returns.
//
// Not in the table on purpose: what GPC does. A Global Privacy Control signal
// turns advertising off in EVERY region (consent.ts), so no row can opt out of
// it; a row only decides whether the banner says so (`ui.gpcHonoured`).
// Bump `version` when a row's meaning changes: receipts carry `<id>@<version>`.
// ============================================================
const UNDECIDED = {
    analytics: 'unknown',
    personalisation: 'unknown',
    advertising: 'unknown',
};
/** EU-27, the EEA members outside the EU, the UK, and EU outermost regions with their own code. */
// prettier-ignore
const EEA_UK = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
    'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
    'IS', 'LI', 'NO',
    'GB',
    'AX', 'GF', 'GP', 'MQ', 'RE', 'YT', 'MF',
];
export const CONSENT_RULES = [
    {
        id: 'gdpr',
        version: 1,
        label: 'EU, EEA and UK (GDPR, ePrivacy, UK GDPR and PECR): opt-in for every non-essential purpose',
        countries: EEA_UK,
        mode: 'opt_in',
        defaults: UNDECIDED,
        ui: {
            layout: 'choices',
            itemised: false,
            reopenButton: true,
            grievance: false,
            gpcHonoured: false,
            ageConfirm: [],
        },
    },
    {
        id: 'us',
        version: 1,
        label: 'United States (state privacy laws): notice plus opt-out of sale and sharing; GPC honoured',
        countries: ['US'],
        mode: 'opt_out',
        defaults: { analytics: 'granted', personalisation: 'granted', advertising: 'granted' },
        ui: {
            layout: 'notice',
            itemised: false,
            reopenButton: true,
            grievance: false,
            gpcHonoured: true,
            ageConfirm: [],
        },
    },
    {
        id: 'in-dpdp',
        version: 1,
        label: 'India (DPDP Act 2023): itemised opt-in, withdrawal as easy as consent, grievance route, 18+ confirmation for personalisation and advertising',
        countries: ['IN'],
        mode: 'opt_in',
        defaults: UNDECIDED,
        ui: {
            layout: 'choices',
            itemised: true,
            reopenButton: true,
            grievance: true,
            gpcHonoured: false,
            // DPDP Act § 9: under-18s are children (verifiable parental consent). A self-declared
            // adult line keeps these two denied for anyone who doesn't tick it.
            ageConfirm: ['personalisation', 'advertising'],
        },
    },
    {
        id: 'default',
        version: 1,
        label: 'Everywhere else: opt-in for every non-essential purpose',
        countries: [],
        mode: 'opt_in',
        defaults: UNDECIDED,
        ui: {
            layout: 'choices',
            itemised: false,
            reopenButton: true,
            grievance: false,
            gpcHonoured: false,
            ageConfirm: [],
        },
    },
];
/** `<id>@<version>`: what receipts and reports carry. */
export function ruleRef(rule) {
    return `${rule.id}@${rule.version}`;
}
/**
 * The rule for a country (Cloudflare `cf.country`). Unknown, Tor (`T1`) and `XX` fall back to
 * the row without countries.
 */
export function consentRuleFor(country, rules = CONSENT_RULES) {
    const cc = typeof country === 'string' ? country.trim().toUpperCase() : '';
    return ((cc ? rules.find(r => r.countries.includes(cc)) : undefined) ??
        rules.find(r => r.countries.length === 0) ??
        rules[rules.length - 1]);
}
/**
 * The rule for a request. A country Cloudflare reports (`request.cf.country`) is used as is. A
 * country forwarded by a proxy in front of ingest is a client-side claim (docs/first-party.md):
 * it may only make the rule stricter. When it maps to an opt-out rule, the fallback (opt-in) row
 * applies, so nobody can weaken a visitor's defaults by claiming a country.
 */
export function consentRuleForRequest(country, opts = {}, rules = CONSENT_RULES) {
    const rule = consentRuleFor(country, rules);
    if (opts.forwarded && rule.mode === 'opt_out') {
        return consentRuleFor('', rules);
    }
    return rule;
}
/** Countries outside YOM Intent: the EEA and the UK (the gdpr rule's list) and Switzerland. */
const INTENT_EXCLUDED_COUNTRIES = [...EEA_UK, 'CH'];
/**
 * YOM Intent is not offered to end users in the EEA, the UK, Switzerland or Quebec (counsel
 * determinations B12, E23 and G38): no profile, no score, no export. Decided at ingest from
 * Cloudflare's `request.cf.country` and `request.cf.regionCode` (or what the member's
 * first-party proxy or backend forwards). Canada without a region code counts as Quebec, which
 * can't be ruled out. No country at all decides nothing here.
 */
export function intentExcludedRegion(country, regionCode) {
    const cc = typeof country === 'string' ? country.trim().toUpperCase() : '';
    if (cc !== 'CA') {
        return intentExcludedCountry(cc);
    }
    const rc = typeof regionCode === 'string' ? regionCode.trim().toUpperCase() : '';
    return !rc || rc === 'QC';
}
/** The country part of intentExcludedRegion, for stored profiles (a country, no region). */
export function intentExcludedCountry(country) {
    return (typeof country === 'string' &&
        INTENT_EXCLUDED_COUNTRIES.includes(country.trim().toUpperCase()));
}
/** Structural checks for a rule table (unit-tested against CONSENT_RULES). */
export function validateConsentRules(rules) {
    const issues = [];
    const seen = new Map();
    const ids = new Set();
    const states = new Set(['granted', 'denied', 'unknown']);
    for (const r of rules) {
        if (!/^[a-z][a-z0-9-]{1,31}$/.test(r.id) || ids.has(r.id)) {
            issues.push(`rule ${r.id}: invalid or duplicate id`);
        }
        ids.add(r.id);
        if (!Number.isInteger(r.version) || r.version < 1) {
            issues.push(`rule ${r.id}: version must be a positive integer`);
        }
        for (const cc of r.countries) {
            if (!/^[A-Z]{2}$/.test(cc)) {
                issues.push(`rule ${r.id}: ${cc} is not an ISO 3166-1 alpha-2 code`);
            }
            const other = seen.get(cc);
            if (other) {
                issues.push(`${cc} is in both ${other} and ${r.id}`);
            }
            seen.set(cc, r.id);
        }
        for (const p of ['analytics', 'personalisation', 'advertising']) {
            if (!states.has(r.defaults[p])) {
                issues.push(`rule ${r.id}: defaults.${p} invalid`);
            }
        }
        if (r.mode === 'opt_in' && Object.values(r.defaults).some(v => v === 'granted')) {
            issues.push(`rule ${r.id}: an opt-in rule cannot default a purpose to granted`);
        }
        for (const p of r.ui.ageConfirm) {
            if (!['analytics', 'personalisation', 'advertising'].includes(p)) {
                issues.push(`rule ${r.id}: ui.ageConfirm has unknown purpose ${p}`);
            }
            if (r.defaults[p] === 'granted') {
                issues.push(`rule ${r.id}: ${p} needs an age confirmation, so it cannot default to granted`);
            }
        }
        if (r.ui.ageConfirm.length > 0 && !r.ui.itemised) {
            issues.push(`rule ${r.id}: ui.ageConfirm needs ui.itemised (the line sits by the toggles)`);
        }
    }
    if (rules.filter(r => r.countries.length === 0).length !== 1) {
        issues.push('exactly one fallback rule (no countries) is required');
    }
    return issues;
}
//# sourceMappingURL=consent-rules.js.map